import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AlertController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import { isNull } from 'lodash-es';
import { switchMap, withLatestFrom } from 'rxjs';
import { catchError, concatMap, filter, map } from 'rxjs/operators';

import { WalkingFarmMainTabEnum } from '../../enums/walking-farm';
import { AdminPortalApiService } from '../../misc/services/adminPortalApi.service';
import * as fromPushNotificationsActions from '../actions/push-notifications.actions';
import * as fromUserActions from '../actions/user.actions';
import * as fromWalkingFarmActions from '../actions/walking-farm.actions';
import * as fromPushNotificationsSelectors from '../selectors/push-notifications.selectors';

@Injectable()
export class PushNotificationsEffects {
  private actions$ = inject(Actions);
  private adminPortalApiService = inject(AdminPortalApiService);
  private alertController = inject(AlertController);
  private router = inject(Router);
  private store = inject(Store);

  registrationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPushNotificationsActions.registrationSuccess),
      map(({ payload }) =>
        fromUserActions.updateMobileUser({
          payload: {
            newFirebaseToken: payload.value
          }
        })
      )
    )
  );

  notificationReceived$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPushNotificationsActions.notificationReceived),
        concatMap(async ({ payload }) => {
          const alert = await this.alertController.create({
            backdropDismiss: false,
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: payload.title,
            message: payload.body
          });
          await alert.present();
        })
      ),
    {
      dispatch: false
    }
  );

  notificationActionPerformed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPushNotificationsActions.notificationActionPerformed),
      filter(({ payload }) => payload.notification.data.smdType === 'farmAlerts'),
      concatMap(async () => {
        await this.router.navigate(['/walking-farm']);
        return fromWalkingFarmActions.setMainSelectedTab({
          payload: { selectedTab: WalkingFarmMainTabEnum.Saved }
        });
      })
    )
  );

  sendTestNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromPushNotificationsActions.sendTestNotification),
        withLatestFrom(this.store.pipe(select(fromPushNotificationsSelectors.selectToken))),
        filter((results) => !isNull(results[1])),
        switchMap(([{ payload }, token]) =>
          this.adminPortalApiService
            .sendRequest('POST', `/v2/push-notifications`, {
              body: {
                message: {
                  data: {
                    smdType: payload.type
                  },
                  notification: {
                    body: 'This is a debug test for push notifications.',
                    title: 'Push Notification Test'
                  },
                  token: token?.value
                },
                options: {
                  delay: 10000
                }
              }
            })
            .pipe(
              switchMap(async () => {
                const alert = await this.alertController.create({
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Success',
                  message: 'You have sent a push notification to this device. It will arrive shortly.'
                });
                await alert.present();
              }),
              catchError(async (error: HttpErrorResponse) => {
                const alert = await this.alertController.create({
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Oops',
                  message: error.message
                });
                await alert.present();
              })
            )
        )
      ),
    {
      dispatch: false
    }
  );
}
